import { useEffect, useRef, useState } from "react"

import { MAX_RATING } from "../static"

let nextKey = 1
const newTruck = () => ({ key: `n${nextKey++}`, ids: [], count: "", units: "" })

const numero = value => {
  const parsed = parseFloat(value)
  return isNaN(parsed) ? 0 : parsed
}

const entero = value => {
  const parsed = parseInt(value, 10)
  return isNaN(parsed) ? 0 : parsed
}

/**
 * El backend guarda una fila por camión en `daily_availability_quantities`.
 * La pantalla trabaja con grupos «N camiones de X unidades», así que al leer
 * un registro se agrupan las filas que comparten unidades.
 */
export const agruparCamiones = quantities => {
  const grupos = []
  ;(quantities || []).forEach(quantity => {
    const units = String(quantity.truck_units ?? "")
    const grupo = grupos.find(item => item.units === units)
    if (grupo) {
      grupo.ids.push({ id: quantity.id, truck_name: quantity.truck_name })
      grupo.count = String(grupo.ids.length)
    } else {
      grupos.push({
        key: `q${quantity.id}`,
        ids: [{ id: quantity.id, truck_name: quantity.truck_name }],
        count: "1",
        units,
      })
    }
  })
  return grupos.length ? grupos : [newTruck()]
}

/**
 * Estado de un formulario de disponibilidad: sirve tanto al alta como a la
 * edición inline de un registro existente.
 *
 * @param {object|null} daily   registro existente (null en el alta)
 * @param {array} regions       lista plana de zonas de la sucursal
 */
export default function useAvailabilityForm(daily, regions) {
  const construirEstado = () => ({
    pesoMin: daily ? String(daily.weight ?? "") : "",
    pesoMax: daily ? String(daily.weight_max ?? "") : "",
    pigmento: daily ? Number(daily.quality) || 0 : 0,
    calidad: daily ? Number(daily.quality_2) || 0 : 0,
    salud: daily ? Number(daily.quality_3) || 0 : 0,
    observaciones: daily ? daily.observations || "" : "",
    trucks: daily ? agruparCamiones(daily.quantities) : [newTruck()],
    prices: construirPrecios(daily, regions),
  })

  const [state, setState] = useState(construirEstado)
  const [error, setError] = useState("")

  // Precios capturados en el modal de sub-zonas: se aplican en cuanto la lista
  // de zonas se refresca y el formulario se reconstruye
  const pendientes = useRef(null)

  // Al cambiar de registro o de sucursal el formulario se rehace por completo
  useEffect(() => {
    setState(() => {
      const base = construirEstado()
      if (!pendientes.current) return base

      const prices = { ...base.prices }
      Object.keys(pendientes.current).forEach(regionId => {
        prices[regionId] = { ...prices[regionId], ...pendientes.current[regionId] }
      })
      pendientes.current = null
      return { ...base, prices }
    })
    setError("")
    // eslint-disable-next-line
  }, [daily, regions])

  const set = (campo, value) => {
    setError("")
    setState(previo => ({ ...previo, [campo]: value }))
  }

  const setPrecio = (regionId, columna, value) => {
    setError("")
    setState(previo => ({
      ...previo,
      prices: {
        ...previo.prices,
        [regionId]: { ...previo.prices[regionId], [columna]: value },
      },
    }))
  }

  /**
   * Deja preparados los precios capturados en el modal de sub-zonas. No se
   * aplican aquí: al guardar sub-zonas cambia la lista de zonas, lo que
   * reconstruye el formulario, y es esa reconstrucción la que los incorpora.
   */
  const aplicarPrecios = mapa => {
    pendientes.current = mapa
  }

  const addTruck = () => {
    setError("")
    setState(previo => ({ ...previo, trucks: [...previo.trucks, newTruck()] }))
  }

  const removeTruck = key => {
    setError("")
    setState(previo => ({
      ...previo,
      trucks: previo.trucks.filter(truck => truck.key !== key),
    }))
  }

  const setTruck = (key, campo, value) => {
    setError("")
    setState(previo => ({
      ...previo,
      trucks: previo.trucks.map(truck =>
        truck.key === key ? { ...truck, [campo]: value } : truck
      ),
    }))
  }

  const reset = () => {
    setState(construirEstado())
    setError("")
  }

  /** Devuelve el mensaje del primer problema encontrado, o "" si todo está bien. */
  const validar = () => {
    if (state.pesoMin === "" || state.pesoMax === "")
      return "Debes indicar el peso mínimo y el peso máximo."
    if (numero(state.pesoMax) < numero(state.pesoMin))
      return "El peso máximo no puede ser menor que el peso mínimo."
    if (
      !state.pigmento ||
      !state.calidad ||
      !state.salud ||
      state.pigmento > MAX_RATING ||
      state.calidad > MAX_RATING ||
      state.salud > MAX_RATING
    )
      return "Debes calificar pigmento, calidad y salud del ave."

    const utiles = state.trucks.filter(
      truck => entero(truck.count) > 0 && numero(truck.units) > 0
    )
    if (utiles.length === 0)
      return "Debes registrar al menos un camión con unidades mayores a 0."

    // el backend no borra filas de daily_availability_quantities
    const recortado = state.trucks.some(
      truck => truck.ids.length > 0 && entero(truck.count) < truck.ids.length
    )
    if (recortado)
      return "No puedes reducir el número de camiones ya registrados."

    const algunPrecio = Object.values(state.prices).some(
      precio => numero(precio.destino) > 0 || numero(precio.granja) > 0
    )
    if (!algunPrecio) return "Debes colocar algún precio mayor a 0."

    return ""
  }

  /**
   * Arma el elemento de `data[]` que esperan POST y PUT
   * /distribuitor/provider/daily-available.
   */
  const construirRegistro = ({ farmId, product }) => {
    const trucks = []
    const quantities = []

    state.trucks.forEach(truck => {
      const count = entero(truck.count)
      const units = truck.units
      if (count <= 0 || numero(units) <= 0) return

      // los camiones ya guardados se actualizan por id
      truck.ids.forEach(existente =>
        quantities.push({
          id: existente.id,
          truck_name: existente.truck_name,
          truck_units: units,
        })
      )

      // el resto son altas: el backend expande `quantity` en una fila por camión
      const porCrear = count - truck.ids.length
      if (porCrear > 0) trucks.push({ quantity: porCrear, truck_units: units })
    })

    const registro = {
      farm_id: farmId,
      product: product.name,
      product_id: product.id,
      weight: state.pesoMin,
      weight_max: state.pesoMax,
      quality: state.pigmento,
      quality_2: state.calidad,
      quality_3: state.salud,
      observations: state.observaciones,
      trucks,
      prices: regions
        .map(region => {
          const precio = state.prices[region.id] || {}
          const destino = numero(precio.destino)
          const granja = numero(precio.granja)
          if (destino === 0 && granja === 0) return null
          return {
            region_id: region.parent_id || region.id,
            subregion_id: region.parent_id ? region.id : null,
            price_shipped: destino,
            price_local: granja,
            shipped: destino > 0,
            local: granja > 0,
          }
        })
        .filter(Boolean),
    }

    if (daily) {
      registro.id = daily.id
      registro.quantities = quantities
    }

    return registro
  }

  return {
    state,
    error,
    setError,
    events: {
      set,
      setPrecio,
      aplicarPrecios,
      addTruck,
      removeTruck,
      setTruck,
      reset,
    },
    validar,
    construirRegistro,
  }
}

// Un precio nulo o en cero se muestra como campo vacío, no como "0.00"
function aCampo(value) {
  if (value === null || value === undefined || value === "") return ""
  return parseFloat(value) === 0 ? "" : String(value)
}

function construirPrecios(daily, regions) {
  const prices = {}
  ;(regions || []).forEach(region => {
    prices[region.id] = { destino: "", granja: "" }
  })

  if (!daily) return prices
  ;(daily.prices || []).forEach(price => {
    // una fila de precios apunta a la subzona si la hay, si no a la zona padre
    const regionId = price.subregion_id || price.region_id
    prices[regionId] = {
      destino: aCampo(price.price_shipped),
      granja: aCampo(price.price_local),
    }
  })

  return prices
}
